import React from 'react';
import {
  Grid,
  TextField as AutoCompleteTextField,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import { FieldLabel } from '@roc/ui';
import { observer } from 'mobx-react-lite';
import TitleAgentV2Store, {
  TitleCompanySelection,
} from '../../../stores/documentForms/titleAgentV2/titleAgentV2Store';

interface TitleCompanySearchProps {
  store: TitleAgentV2Store;
  disabled?: boolean;
}

interface AddNewOption {
  isAddNew: true;
  name: string;
}

type SearchOption = TitleCompanySelection | AddNewOption;

const baseFilter = createFilterOptions<TitleCompanySelection>({
  matchFrom: 'any',
  stringify: (option) => option.name,
});

const filterOptions = (options: SearchOption[], params: any): SearchOption[] => {
  const filtered = baseFilter(options as TitleCompanySelection[], params);
  if (filtered.length === 0 && params.inputValue.trim()) {
    return [{ isAddNew: true, name: params.inputValue.trim() }];
  }
  return filtered;
};

const TitleCompanySearch = observer(
  ({ store, disabled }: TitleCompanySearchProps) => {
    return (
      <Grid item xs={12}>
        <FieldLabel>Title company *</FieldLabel>
        <Autocomplete
          style={{ width: '100%' }}
          id="titleCompanySearch"
          options={store.titleCompanies as SearchOption[]}
          freeSolo
          disabled={disabled}
          filterOptions={filterOptions}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') return option;
            if (option?.isAddNew) return '';
            return option?.name ?? '';
          }}
          value={null}
          inputValue={store.searchText}
          onChange={(_event, newValue: any) => {
            if (!newValue || typeof newValue === 'string') return;
            if (newValue.isAddNew) {
              store.openCreateForm(store.searchText.trim());
            } else {
              store.selectExistingCompany(newValue as TitleCompanySelection);
            }
          }}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') {
              store.setSearchText(value);
            } else if (reason === 'clear') {
              store.changeCompany();
            }
            // 'reset' is handled by onChange (selectExistingCompany / openCreateForm)
          }}
          renderOption={(option: any) => {
            if (option.isAddNew) {
              return (
                <Grid container direction="column" style={{ padding: '4px 0' }}>
                  <Typography style={{ color: 'rgba(0,0,0,0.54)', marginBottom: 8 }}>
                    No matching companies
                  </Typography>
                  <Typography color="primary">
                    {`+ Add "${option.name}" as a new title company`}
                  </Typography>
                </Grid>
              );
            }
            return (
              <Grid container direction="column">
                <Typography variant="body1">{option.name}</Typography>
                {option.address && (
                  <Typography variant="body2" color="textSecondary">
                    {option.address}
                  </Typography>
                )}
              </Grid>
            );
          }}
          renderInput={params => (
            <AutoCompleteTextField
              {...params}
              variant="outlined"
              placeholder="Search by company name"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
    );
  }
);

export default TitleCompanySearch;
